import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout, Section, Eyebrow, FinalCTA, Breadcrumbs } from "@/components/site/Layout";
import { caseStudies } from "@/data/case-studies";
import { breadcrumbSchema, jsonLdScript, organizationSchema } from "@/lib/seo";

export const Route = createFileRoute("/case-studies/")({
  head: () => ({
    meta: [
      { title: "Law Firm SEO Case Studies | Real Rankings, Real Cases" },
      { name: "description", content: "Real case studies from law firms we've ranked. Personal injury, family law, DUI, criminal defense, estate planning, measurable, attributable results." },
      { property: "og:title", content: "Law Firm SEO Case Studies | PageOne Lawyers" },
      { property: "og:description", content: "Real rankings. Real cases. Real attribution." },
      { property: "og:url", content: "https://pageonelawyers.com/case-studies" },
    ],
    links: [{ rel: "canonical", href: "https://pageonelawyers.com/case-studies" }],
    scripts: [
      jsonLdScript(organizationSchema()),
      jsonLdScript(breadcrumbSchema([
        { label: "Home", path: "/" },
        { label: "Case Studies", path: "/case-studies" },
      ])),
    ],
  }),
  component: CaseStudies,
});

function CaseStudies() {
  return (
    <Layout>
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Case Studies" }]} />
          <Eyebrow><span className="text-[var(--gold)]">Case Studies</span></Eyebrow>
          <h1 className="mt-6 font-display text-5xl md:text-6xl font-semibold max-w-3xl">Real firms. Real rankings. Real cases.</h1>
          <p className="mt-6 text-lg text-primary-foreground/80 max-w-2xl">Every metric below comes from a working client engagement. Firm names withheld; outcomes verifiable on request under NDA.</p>
        </div>
      </section>
      <Section>
        <div className="grid md:grid-cols-2 gap-8">
          {caseStudies.map((s) => (
            <Link
              key={s.slug}
              to="/case-studies/$slug"
              params={{ slug: s.slug }}
              className="group rounded-2xl overflow-hidden border border-border bg-card shadow-[var(--shadow-card)] hover:border-[var(--gold)] transition flex flex-col"
            >
              <div className="aspect-[16/10] overflow-hidden bg-secondary">
                <img
                  src={s.cover}
                  alt={`${s.practice} case study, ${s.market}`}
                  width={1400}
                  height={900}
                  loading="lazy"
                  className="h-full w-full object-cover group-hover:scale-[1.03] transition duration-500"
                />
              </div>
              <div className="p-6 md:p-8 flex-1 flex flex-col">
                <div className="flex flex-wrap gap-3 items-center mb-3">
                  <span className="rounded-full bg-primary text-primary-foreground px-3 py-1 text-xs font-semibold">{s.practice}</span>
                  <span className="text-sm text-muted-foreground">{s.market}</span>
                </div>
                <h2 className="font-display text-2xl md:text-[26px] font-semibold leading-snug">{s.headline}</h2>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {s.metrics.map(([m, l]) => (
                    <div key={l} className="rounded-lg bg-secondary/60 p-3 text-center">
                      <div className="font-display text-lg md:text-xl font-semibold text-[var(--gold-deep)]">{m}</div>
                      <div className="mt-1 text-[11px] text-muted-foreground leading-tight">{l}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 text-sm font-semibold text-primary group-hover:text-[var(--gold-deep)]">Read the full case study →</div>
              </div>
            </Link>
          ))}
        </div>
      </Section>
      <FinalCTA title="Want results like these for your firm?" sub="Every engagement starts with a free audit. We'll tell you what's possible, honestly." />
    </Layout>
  );
}